// DeviceRegistrationEnhanced.js
import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { notifySuccess, notifyError } from '../Notification';
import LeafletMapPicker from './LeafletMapPicker';
import { MdAppRegistration, MdAddLocationAlt } from 'react-icons/md'; // Material Design icon

import './deviceSetup.css';

const DeviceRegistration = () => {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [newStoreData, setNewStoreData] = useState({ name: '', url: '' });
  const [showNewSiteForm, setShowNewSiteForm] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const email = localStorage.getItem('user');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const deviceIdFromUrl = urlParams.get('device_id');
    if (deviceIdFromUrl) {
      setDeviceId(deviceIdFromUrl);
    }
  }, []);

  // Helper function to redirect back to tablet
  const redirectToTablet = () => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log(`redirect urlParams = ${urlParams}`);
    let tabletUrl = urlParams.get('tablet_url');

    console.log('🔄 redirectToTablet called');
    console.log('📍 tablet_url from params:', tabletUrl);
    console.log('📍 deviceId:', deviceId);

    if (tabletUrl) {
      // If tablet_url is provided, use it
      const redirectUrl = new URL(tabletUrl);
      // Format: #?registration_success=true&device_id=... (simpler format for HashRouter)
      redirectUrl.hash = '?registration_success=true&device_id=' + encodeURIComponent(deviceId);
      const finalUrl = redirectUrl.toString();
      console.log('✅ Redirecting to tablet with URL:', finalUrl);
      console.log('📍 Hash portion:', redirectUrl.hash);
      window.location.href = finalUrl;
    } else {
      // Fallback: try to use referrer or show message
      console.warn('❌ No tablet_url provided. Redirect back manually or add tablet_url parameter to the admin dashboard link.');
      notifySuccess('Device registration complete! Please return to your tablet device.');
    }
  };

  useEffect(() => {
    if (email) {
      api.get(`/user-stores?email=${email}`)
        .then(res => setStores(res.data))
        .catch(console.error);
    }
  }, [email]);

  const getGeolocation = () => {
    navigator.geolocation.getCurrentPosition(
      position => setLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
      err => console.warn('Geolocation denied or failed:', err),
      { enableHighAccuracy: true }
    );
  };

  const handleDeviceRegister = () => {
    setIsRegistering(true);
    const payload = {
      email,
      device_id: deviceId,
      store_id: selectedStoreId,
      device_name: deviceName || null,
      latitude: location.lat,
      longitude: location.lng,
    };

    api.post('/register-device', payload)
      .then(() => {
        const store = stores.find(s => s.store_id === selectedStoreId);
        notifySuccess(`Device registered to store: ${store?.name}`);

        // Redirect back to tablet after 1.5 seconds
        setTimeout(() => {
          redirectToTablet();
        }, 1500);
      })
      .catch(err => {
        if (err.response && err.response.status === 409) {
          notifyError(err.response.data.message);
        } else {
          console.error(err);
          notifyError('An unexpected error occurred during device registration.');
        }
        setIsRegistering(false);
      });
  };

  const handleNewStoreAdd = () => {
    setIsRegistering(true);
    const storePayload = {
      name: newStoreData.name,
      google_review_url: newStoreData.url,
      latitude: location.lat,
      longitude: location.lng,
    };

    api.post('/add-store-and-register', {
      email,
      device_id: deviceId,
      device_name: deviceName || null,
      ...storePayload
    })
      .then(() => {
        notifySuccess(`Store ${newStoreData.name} added and device registered.`);

        // Redirect back to tablet after 1.5 seconds
        setTimeout(() => {
          redirectToTablet();
        }, 1500);
      })
      .catch(err => {
        console.error(err);
        notifyError('An error occurred during store and device registration.');
        setIsRegistering(false);
      });
  };

  const handleBackToMain = () => {
    setShowNewSiteForm(false);
  };

  return (
    <div className="device-register-page">
      <div className="device-register-container">
        {!showNewSiteForm ? (
          <div className="register-card">
            <div className="register-header gradient-purple-blue">
             <div className="header-with-icon">
                <MdAppRegistration className="header-icon" />
               <span> Register Your Device</span>
              </div>
            </div>


            <div className="register-body">
              <label className="site-list-label" htmlFor="store-selection">
                📍 Choose a site from the list below:
              </label>
              <div className="store-list">
                {stores.map(store => (
                  <label
                    key={store.store_id}
                    className={`store-item ${selectedStoreId === store.store_id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="store"
                      value={store.store_id}
                      checked={selectedStoreId === store.store_id}
                      onChange={() => setSelectedStoreId(store.store_id)}
                      className="store-radio"
                    />
                    <span className="store-name">{store.name}</span>
                  </label>
                ))}
              </div>

              <div className="divider-with-text">
                <span>or</span>
              </div>

              <button className="btn btn-gradient" onClick={() => setShowNewSiteForm(true)}>
                + Create New Site (if not listed)
              </button>

              {selectedStoreId && (
                <div className="register-actions">
                  <input
                    type="text"
                    placeholder="Device Name (optional)"
                    value={deviceName}
                    onChange={e => setDeviceName(e.target.value)}
                  />
                  <button className="btn-location" onClick={getGeolocation}>📍 Use Current Location</button>
                  <button className="btn btn-dark" onClick={handleDeviceRegister} disabled={isRegistering}>
                    {isRegistering ? 'Registering...' : 'Register Device'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="register-card">
            <div className="register-header gradient-green-blue">
              <button className="btn-back" onClick={handleBackToMain}>←</button>
              <h1 className="header-with-icon">
                <MdAddLocationAlt className="header-icon" />
                Add New Site
              </h1>
            </div>

            <div className="register-body">
              <input
                type="text"
                placeholder="Store Name"
                required
                value={newStoreData.name}
                onChange={e => setNewStoreData({ ...newStoreData, name: e.target.value })}
              />
              <input
                type="url"
                placeholder="Google Review URL (optional)"
                value={newStoreData.url}
                onChange={e => setNewStoreData({ ...newStoreData, url: e.target.value })}
              />
              <input
                type="text"
                placeholder="Device Name (optional)"
                value={deviceName}
                onChange={e => setDeviceName(e.target.value)}
              />
              <div className='map-container'><LeafletMapPicker onSelect={setLocation} /></div>
              <button
                className="btn btn-green"
                onClick={handleNewStoreAdd}
                disabled={isRegistering || !newStoreData.name}
              >
                {isRegistering ? 'Creating & Registering…' : 'Create Store & Register Device'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceRegistration;
